import { useMemo, useState } from "react";
import { Check, LoaderCircle } from "lucide-react";
import { useIntlLocale, useT } from "@shared/client/locale";
import { useRouter } from "@shared/client/navigation";
import type { OfferSummary } from "@shared/lib/offers-shared";
import { Alert, AlertDescription } from "@shared/components/ui/alert";
import { Label } from "@shared/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/components/ui/select";
import { Switch } from "@shared/components/ui/switch";
import { UpgradeLink } from "@/components/billing/plan-limit";

/**
 * La veille d'une offre : tourne-t-elle, à quelle cadence, et quand est le
 * prochain passage. La cadence existait en base depuis la migration 004
 * (`watched_offers.frequency_days`) sans jamais être exposée — l'interface
 * affirmait « une analyse par semaine » quelle que soit la valeur réelle.
 *
 * Chaque offre a la sienne : un produit à cycle long ne se surveille pas au
 * même rythme qu'un marché mouvant.
 */

// Le libellé se lit entier dans le champ replié : la nuance (« pour marchés
// mouvants ») est une aide au choix, pas une étiquette à traîner ensuite.
// Stored number of days; labels in `settings.watch.cadenceOptions`.
const CADENCES = [3, 7, 14, 30] as const;

// Plan le plus petit qui permet chaque cadence (le minimum vient de `plans`).
const planFor = (days: number) => (days >= 7 ? "solo" : "pro");

export function WatchControls({ offer, minFrequencyDays = 1 }: { offer: OfferSummary; minFrequencyDays?: number }) {
  const messages = useT().settings;
  const t = messages.watch;
  const intlLocale = useIntlLocale();
  // Le jour du radar, à Paris : un lecteur à l'autre bout du monde lit le même.
  const dayFormat = useMemo(
    () => new Intl.DateTimeFormat(intlLocale, { weekday: "long", day: "numeric", month: "long", timeZone: "Europe/Paris" }),
    [intlLocale],
  );
  const router = useRouter();
  const [isActive, setIsActive] = useState(offer.radarActive);
  const [frequency, setFrequency] = useState(String(offer.frequencyDays));
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  async function save(next: { active?: boolean; frequencyDays?: number }) {
    setBusy(true);
    setError("");
    setMessage("");
    try {
      const response = await fetch(`/api/offers/${offer.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(next),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || t.saveFailed);
      setIsActive(data.offer?.radarActive ?? next.active ?? isActive);
      // Le prochain passage suit la veille et sa cadence : la vue le recalcule.
      router.refresh();
      setMessage((next.active ?? isActive) ? t.saved : t.paused);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t.saveFailed);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="grid gap-4">
      <div className="flex flex-wrap items-center gap-x-8 gap-y-4">
        <label className="flex items-center gap-3">
          <Switch
            aria-label={t.switchLabel(offer.name)}
            checked={isActive}
            disabled={busy}
            onCheckedChange={(next) => {
              setIsActive(next);
              void save({ active: next });
            }}
          />
          <span className="text-[13px] font-semibold">{t.switchText}</span>
        </label>

        <div className="grid gap-2">
          <Label className="text-[12px] font-semibold text-muted-foreground" htmlFor={`cadence-${offer.id}`}>
            {t.cadence}
          </Label>
          <Select
            disabled={busy || !isActive}
            onValueChange={(value) => {
              setFrequency(value);
              void save({ frequencyDays: Number(value) });
            }}
            value={frequency}
          >
            <SelectTrigger className="h-9 w-[268px] text-[13px]" id={`cadence-${offer.id}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CADENCES.map((days) => {
                const locked = days < minFrequencyDays;
                return (
                  <SelectItem disabled={locked} key={days} value={String(days)}>
                    {t.cadenceOptions[days]}
                    {locked ? t.lockedSuffix(messages.plans.names[planFor(days)]) : ""}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>

        {busy && <LoaderCircle className="size-4 animate-spin text-muted-foreground" aria-hidden="true" />}
      </div>

      {minFrequencyDays >= 30 && (
        <p className="m-0 text-[12px] text-muted-foreground">
          {t.freePlanNote} <UpgradeLink label={t.freePlanUpgrade} />
        </p>
      )}

      <p className="m-0 text-[12px] text-muted-foreground">
        {offer.lastPassAt ? t.lastPass(dayFormat.format(new Date(offer.lastPassAt))) : t.noPass}
        {isActive && offer.nextPassAt && ` ${t.nextPass(dayFormat.format(new Date(offer.nextPassAt)))}`}
        {isActive && !offer.covered && (
          <>
            {" "}
            {t.notCovered} <UpgradeLink />
          </>
        )}
      </p>

      {message && (
        <p className="m-0 flex items-center gap-2 text-[12.5px] font-medium text-success">
          <Check className="size-4" aria-hidden="true" /> {message}
        </p>
      )}
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
    </div>
  );
}
